import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import { TenantLogo } from './TenantLogo';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml';
const MAX_BYTES = 512 * 1024;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function TenantLogoUpload({
  tenantCode,
  tenantName,
  color,
  logoUrl,
  onChange,
}: {
  tenantCode: string;
  tenantName?: string;
  color?: string | null;
  logoUrl?: string | null;
  onChange?: (logoUrl: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localLogoUrl, setLocalLogoUrl] = useState<string | null>(logoUrl ?? null);

  const displayLogoUrl = localLogoUrl ?? logoUrl ?? null;
  const previewTenant = {
    code: tenantCode,
    name: tenantName,
    color,
    logoUrl: displayLogoUrl,
  };

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setError(null);

    if (!file.type || !ACCEPT.split(',').includes(file.type)) {
      setError('Use PNG, JPEG, WebP, GIF, or SVG.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Logo must be 512 KB or smaller.');
      return;
    }

    setUploading(true);
    try {
      const imageBase64 = await readFileAsBase64(file);
      const result = await apiClient.uploadTenantLogo(tenantCode, imageBase64, file.type);
      const baseUrl = result.logoUrl ?? displayLogoUrl;
      const nextUrl = baseUrl ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${Date.now()}` : null;
      setLocalLogoUrl(nextUrl);
      onChange?.(nextUrl ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setError(null);
    setRemoving(true);
    try {
      await apiClient.deleteTenantLogo(tenantCode);
      setLocalLogoUrl(null);
      onChange?.(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <TenantLogo tenant={previewTenant} size="lg" rounded="lg" />
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" disabled={uploading || removing} onClick={handlePick}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ImagePlus className="h-3.5 w-3.5 mr-1" />}
            {displayLogoUrl ? 'Replace logo' : 'Upload logo'}
          </Button>
          {displayLogoUrl ? (
            <Button type="button" size="sm" variant="ghost" disabled={uploading || removing} onClick={handleRemove}>
              {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
              Remove
            </Button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
        />
      </div>
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        PNG, JPEG, WebP, GIF, or SVG — max 512 KB. Shown wherever the tenant name appears.
      </p>
      {error ? <p className="text-[11px] text-red-600 dark:text-red-400">{error}</p> : null}
    </div>
  );
}
